import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  FileInput,
  FileField,
  required,
  useNotify,
  useRedirect,
} from "react-admin";
import { Box, Typography, Paper } from "@mui/material";
import { Upload, Image, Video } from "lucide-react";

// Category choices
const categoryChoices = [
  { id: "portfolio", name: "Portfolio" },
  { id: "hero", name: "Hero" },
  { id: "testimonial", name: "Testimonial" },
  { id: "team", name: "Team" },
  { id: "service", name: "Service" },
  { id: "blog", name: "Blog" },
  { id: "gallery", name: "Gallery" },
  { id: "other", name: "Other" },
];

/**
 * Custom dropzone placeholder
 */
const DropzonePlaceholder = () => (
  <Paper
    sx={{
      p: 4,
      textAlign: "center",
      backgroundColor: "rgba(34, 211, 238, 0.05)",
      border: "2px dashed rgba(34, 211, 238, 0.3)",
      borderRadius: 2,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "rgba(34, 211, 238, 0.1)",
        borderColor: "rgba(34, 211, 238, 0.5)",
      },
    }}
  >
    <Upload size={48} color="#22d3ee" style={{ marginBottom: 16 }} />
    <Typography variant="h6" sx={{ color: "#22d3ee", mb: 1 }}>
      Drop a file here or click to upload
    </Typography>
    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
      Supported formats: JPG, PNG, GIF, WebP, MP4, WebM
    </Typography>
    <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Image size={16} color="#22d3ee" />
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          Images up to 10MB
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Video size={16} color="#a855f7" />
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          Videos up to 100MB
        </Typography>
      </Box>
    </Box>
  </Paper>
);

/**
 * MediaCreate - Form for uploading new media to Cloudinary
 */
export const MediaCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify("Media uploaded successfully", { type: "success" });
    redirect("list", "media");
  };

  const onError = (error: Error) => {
    notify(`Upload failed: ${error.message}`, { type: "error" });
  };

  return (
    <Create
      mutationOptions={{ onSuccess, onError }}
      sx={{
        "& .RaCreate-main": {
          backgroundColor: "#1a1a2e",
        },
      }}
    >
      <SimpleForm
        sx={{
          "& .MuiInputBase-root": {
            backgroundColor: "rgba(255,255,255,0.05)",
          },
          "& .MuiInputLabel-root": {
            color: "#94a3b8",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(34, 211, 238, 0.2)",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(34, 211, 238, 0.4)",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#22d3ee",
          },
        }}
      >
        <Box sx={{ mb: 3, width: "100%" }}>
          <Typography variant="h5" sx={{ color: "#22d3ee", mb: 1 }}>
            Upload New Media
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            Upload images or videos to your media library
          </Typography>
        </Box>

        <FileInput
          source="file"
          label=""
          accept={{ "image/*": [], "video/*": [] }}
          validate={required()}
          placeholder={<DropzonePlaceholder />}
          sx={{
            width: "100%",
            mb: 3,
            "& .RaFileInput-dropZone": {
              backgroundColor: "transparent",
              border: "none",
              padding: 0,
            },
          }}
        >
          <FileField source="src" title="title" />
        </FileInput>

        <TextInput
          source="title"
          label="Title"
          fullWidth
          helperText="A descriptive title for this media"
        />

        <TextInput
          source="description"
          label="Description"
          fullWidth
          multiline
          rows={3}
          helperText="Detailed description (optional)"
        />

        <TextInput
          source="altText"
          label="Alt Text"
          fullWidth
          helperText="Alternative text for accessibility (important for SEO)"
        />

        <SelectInput
          source="category"
          label="Category"
          choices={categoryChoices}
          defaultValue="other"
          fullWidth
          helperText="Where will this media be used?"
        />

        <TextInput
          source="tags"
          label="Tags"
          fullWidth
          helperText="Comma-separated tags for organization (e.g., kitchen, renovation, modern)"
        />

        <SelectInput
          source="featured"
          label="Featured"
          choices={[
            { id: "false", name: "No" },
            { id: "true", name: "Yes" },
          ]}
          defaultValue="false"
          fullWidth
          helperText="Featured media appears prominently on the site"
        />
      </SimpleForm>
    </Create>
  );
};

export default MediaCreate;

